import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  Alert,
  AspectRatio,
  Badge,
  Box,
  Button,
  CloseButton,
  Flex,
  Heading,
  HStack,
  Icon,
  Image as ChakraImage,
  Input,
  Progress,
  RadioGroup,
  NativeSelect,
  Spinner,
  Stack,
  Tag,
  Text,
  Textarea,
  VStack,
  Wrap,
  WrapItem,
  Checkbox,
  useDisclosure,
  Dialog,
  Portal,
  Separator,
  Field,
  Fieldset,
} from '@chakra-ui/react';
import { Radio } from '@/components/ui/chakra-v2-compat';
import { CheckboxGroup, MultiCheckboxField } from '@/components/ui/multi-checkbox-field';
import { toaster } from '@/components/ui/toaster';
import { FaSync, FaLayerGroup, FaTrash, FaPlus, FaSortAmountDownAlt, FaArrowLeft } from 'react-icons/fa';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { etiquetaSlugDesdeNombre } from '../utils/instrumentoEtiqueta';
import { pageImageSrc } from '../utils/staticUrl';
import { bandasAPI } from '../services/api';
import {
  UMBRAL_CONF_ORIENTACION,
  buildDecisionAplicarSugerencia,
  motivoRevisionOrientacion,
  rotacionAplicableDesdeSugerencia,
} from '../utils/partituraOrientacion';

function SortableThumbnail({ page, isSelected, etiquetaResumen, onClick }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: page.id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : undefined,
    position: 'relative',
  };

  return (
    <Box
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      p={1}
      rounded="md"
      borderWidth={isSelected ? 2 : 1}
      borderColor={isSelected ? 'purple.500' : 'gray.200'}
      cursor="grab"
      _active={{ cursor: 'grabbing' }}
      flexShrink={0}
      w={{ base: '76px', lg: 'full' }}
      onClick={onClick}
    >
      <AspectRatio ratio={3 / 4} w="full">
        <Box position="relative" overflow="hidden" borderRadius="md" bg="gray.100" w="full" h="full">
          <ChakraImage
            src={pageImageSrc(page)}
            alt={`Página ${page.orden}`}
            w="100%"
            h="100%"
            objectFit="cover"
            transform={`rotate(${page.rotacion || 0}deg)`}
            transformOrigin="center"
            pointerEvents="none"
          />
          <Text
            position="absolute" top={1} left={1} zIndex={1}
            fontSize="10px" fontWeight="800" lineHeight="1.1"
            color="white" bg="blackAlpha.800"
            px={1.5} py={0.5} rounded="md" pointerEvents="none" boxShadow="sm"
          >
            {page.orden}
          </Text>
          {etiquetaResumen ? (
            <Text
              position="absolute" top={1} right={1} zIndex={1}
              maxW="72%" fontSize="9px" fontWeight="600" lineHeight="1.2"
              color="white" bg="purple.600"
              px={1.5} py={0.5} rounded="md" textAlign="right"
              lineClamp={2} title={etiquetaResumen}
              pointerEvents="none" boxShadow="md"
              borderWidth="1px" borderColor="whiteAlpha.400"
            >
              {etiquetaResumen}
            </Text>
          ) : null}
        </Box>
      </AspectRatio>
    </Box>
  );
}

/**
 * Visor a pantalla completa: panel de etiquetado (izq), miniaturas (centro), página grande (dcha).
 */
export default function BandaRepertorioEtiquetadoPaginasModal({
  isOpen,
  onClose,
  isDirtyModal,
  obraTitulo,
  detail,
  setDetail,
  selectedPage,
  selectedPageId,
  setSelectedPageId,
  paginasOrdenadasEditor,
  ambitoPagina,
  setAmbitoPagina,
  rangoOrdenesTexto,
  setRangoOrdenesTexto,
  rangoPatch,
  setRangoPatch,
  instrumentCatalog,
  vocesCatalog,
  saving,
  nombreVozPorEtiqueta,
  slugVozFila,
  initRangoDesdePagina,
  savePage,
  saveAllPagesEtiquetas,
  rotatePage,
  rotateAllPages90,
  deletePage,
  aplicarEtiquetasVariosTramos,
  setVisorPartituraAbierto,
  bulkActionStatus,
  crearVozMaestro,
  crearInstrumentoMaestro,
  onAnadirPaginas,
}) {
  const railBg = useColorModeValue('gray.50', 'gray.900');
  const railBorder = useColorModeValue('gray.200', 'gray.700');
  const panelBg = useColorModeValue('white', 'gray.800');
  const viewerFooterBg = useColorModeValue('#2d2d2d', '#141414');
  const viewerFooterBorder = useColorModeValue('gray.600', 'gray.700');
  
  const autoInfoModal = useDisclosure();
  const deleteConfirmDialog = useDisclosure();
  const cancelDeleteRef = useRef(null);
  const [pageIdToDelete, setPageIdToDelete] = useState(null);
  const [deletingWithSave, setDeletingWithSave] = useState(false);
  const [autoEtiquetando, setAutoEtiquetando] = useState(false);
  const [aplicandoTodasVerdes, setAplicandoTodasVerdes] = useState(false);
  const [nuevaVozMaestroNombre, setNuevaVozMaestroNombre] = useState('');
  const [nuevoInstMaestroNombre, setNuevoInstMaestroNombre] = useState('');
  const [creandoVozMaestro, setCreandoVozMaestro] = useState(false);
  const [creandoInstMaestro, setCreandoInstMaestro] = useState(false);
  const addMaestroModal = useDisclosure();

  const [ordenandoPorInstrumento, setOrdenandoPorInstrumento] = useState(false);

  const instrumentoOrderKeys = useMemo(() => {
    // Director (guión) primero → maderas → metal → percusión al final.
    const keys = [
      'director',
      'flautin',
      'flauta',
      'oboe',
      'fagot',
      'clarinete',
      'clarinete_bajo',
      'saxofon_soprano',
      'saxofon_alto',
      'saxofon_tenor',
      'saxofon_baritono',
      'saxofon_contrabajo',
      'requinto',
      'trompeta',
      'corneta',
      'corneta_grave',
      'fliscorno',
      'flicorno',
      'trompa',
      'trombon',
      'trombon_bajo',
      'trombon_contrabajo',
      'bombardino',
      'euphonium',
      'eufonio',
      'tuba',
      'sousafon',
      'helicon',
      'caja',
      'redoblante',
      'bombo',
      'platillos',
      'bombo_y_platos',
      'timbales',
      'percusion_auxiliar',
      'percusion_menor',
      'bateria',
      'percusion',
    ];
    const map = new Map();
    keys.forEach((k, i) => map.set(k, i));
    const aliasToCanonical = [
      ['saxo_alto', 'saxofon_alto'],
      ['saxo_tenor', 'saxofon_tenor'],
      // Cabeceras / legado: Bajo ≈ Tuba en banda de música
      ['bajo', 'tuba'],
    ];
    for (const [alias, canonical] of aliasToCanonical) {
      if (map.has(canonical)) map.set(alias, map.get(canonical));
    }
    return map;
  }, []);

  const slugBase = (raw) => String(raw || '').replace(/_\d+$/g, '');

  const instrumentRankFromSlug = useCallback((rawSlug) => {
    const base = slugBase(etiquetaSlugDesdeNombre(String(rawSlug || '')));
    return instrumentoOrderKeys.has(base) ? instrumentoOrderKeys.get(base) : 999;
  }, [instrumentoOrderKeys]);

  const ordenarPaginasPorInstrumento = useCallback(async () => {
    if (!detail?.paginas?.length) return;
    setOrdenandoPorInstrumento(true);
    try {
      // 1) Tomamos el orden actual guardado (por `orden`) para detectar continuaciones.
      const byOrden = [...detail.paginas].sort((a, b) => (a.orden ?? 0) - (b.orden ?? 0));

      /** @type {Map<string, number>} paginaId -> rank */
      const rankByPageId = new Map();
      /** @type {Map<string, string>} paginaId -> display label */
      const labelByPageId = new Map();

      let lastRank = 999;
      let lastLabel = 'Sin etiqueta';

      for (const p of byOrden) {
        const entries = Array.isArray(p.instrumentosVoces) ? p.instrumentosVoces : [];
        if (entries.length) {
          // Si hay varios instrumentos, usamos el de mayor prioridad (menor rank)
          let bestRank = 999;
          let bestLabel = '';
          for (const e of entries) {
            const r = instrumentRankFromSlug(e?.slug || e?.nombre || '');
            if (r < bestRank) {
              bestRank = r;
              bestLabel = String(e?.nombre || e?.slug || '').trim() || 'Instrumento';
            }
          }
          lastRank = bestRank;
          lastLabel = bestLabel || lastLabel;
          rankByPageId.set(p.id, bestRank);
          labelByPageId.set(p.id, bestLabel || lastLabel);
        } else {
          // Continuación: hereda del anterior
          rankByPageId.set(p.id, lastRank);
          labelByPageId.set(p.id, lastLabel);
        }
      }

      // 2) Ordenamos por rank y, dentro de rank, por `orden` original.
      const sorted = [...byOrden].sort((a, b) => {
        const ra = rankByPageId.get(a.id) ?? 999;
        const rb = rankByPageId.get(b.id) ?? 999;
        if (ra !== rb) return ra - rb;
        return (a.orden ?? 0) - (b.orden ?? 0);
      });

      // 3) Reasignamos `orden` consecutivo para que el visor (miniaturas) siga una secuencia estable.
      const newOrderById = new Map(sorted.map((p, idx) => [p.id, idx + 1]));

      // Persistir en servidor (BBDD) y refrescar el detail con la respuesta.
      const res = await bandasAPI.reordenarPaginasObraPartitura(detail.id, { paginaIds: sorted.map((p) => p.id) });
      const fresh = res?.data;
      if (fresh?.paginas?.length) {
        setDetail((prev) => {
          if (!prev?.paginas?.length) return prev;
          const prevById = new Map(prev.paginas.map((p) => [p.id, p]));
          const mergedPaginas = fresh.paginas.map((fp) => {
            const old = prevById.get(fp.id);
            if (!old) return fp;
            return {
              ...fp,
              instrumentosVoces: old.instrumentosVoces,
              notasDirector: old.notasDirector,
              tituloObra: old.tituloObra,
              autor: old.autor,
              ocrTexto: old.ocrTexto,
            };
          });
          return { ...prev, paginas: mergedPaginas };
        });
      } else {
        // Fallback (si algo va raro con la respuesta): al menos orden local.
        setDetail((prev) => {
          if (!prev?.paginas) return prev;
          const nextPages = prev.paginas.map((p) => ({ ...p, orden: newOrderById.get(p.id) ?? p.orden }));
          return { ...prev, paginas: nextPages };
        });
      }

      // Mantén seleccionada la primera página del nuevo orden.
      setSelectedPageId(sorted[0]?.id ?? selectedPageId);

      toaster.create({
        title: 'Páginas ordenadas por instrumento',
        description: 'Orden guardado en el servidor.',
        type: 'success',
        duration: 4000,
      });
    } catch (e) {
      toaster.create({ title: 'No se pudo ordenar', description: e?.message, type: 'error' });
    } finally {
      setOrdenandoPorInstrumento(false);
    }
  }, [detail?.id, detail?.paginas, instrumentRankFromSlug, selectedPageId, setDetail, setSelectedPageId]);

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const pageIds = useMemo(() => paginasOrdenadasEditor.map((p) => p.id), [paginasOrdenadasEditor]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id || !detail?.paginas?.length) return;

    const oldIndex = paginasOrdenadasEditor.findIndex((p) => p.id === active.id);
    const newIndex = paginasOrdenadasEditor.findIndex((p) => p.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(paginasOrdenadasEditor, oldIndex, newIndex);
    const newOrderById = new Map(reordered.map((p, idx) => [p.id, idx + 1]));

    setDetail((prev) => {
      if (!prev?.paginas) return prev;
      return { ...prev, paginas: prev.paginas.map((p) => ({ ...p, orden: newOrderById.get(p.id) ?? p.orden })) };
    });

    try {
      await bandasAPI.reordenarPaginasObraPartitura(detail.id, { paginaIds: reordered.map((p) => p.id) });
    } catch (e) {
      toaster.create({ title: 'No se pudo reordenar', description: e?.message, type: 'error', duration: 3000 });
    }
  }, [detail?.id, detail?.paginas?.length, paginasOrdenadasEditor, setDetail]);

  const unsavedCloseDialog = useDisclosure();
  const cancelUnsavedRef = useRef(null);
  const [closingWithSave, setClosingWithSave] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setNuevaVozMaestroNombre('');
      setNuevoInstMaestroNombre('');
    }
  }, [isOpen]);

  const ejecutarCrearVozMaestro = useCallback(async () => {
    if (!crearVozMaestro) return;
    setCreandoVozMaestro(true);
    try {
      const ok = await crearVozMaestro(nuevaVozMaestroNombre);
      if (ok) setNuevaVozMaestroNombre('');
    } finally {
      setCreandoVozMaestro(false);
    }
  }, [crearVozMaestro, nuevaVozMaestroNombre]);

  const ejecutarCrearInstrumentoMaestro = useCallback(async () => {
    if (!crearInstrumentoMaestro) return;
    setCreandoInstMaestro(true);
    try {
      const ok = await crearInstrumentoMaestro(nuevoInstMaestroNombre);
      if (ok) setNuevoInstMaestroNombre('');
    } finally {
      setCreandoInstMaestro(false);
    }
  }, [crearInstrumentoMaestro, nuevoInstMaestroNombre]);

  const handleCloseRequest = useCallback(() => {
    if (isDirtyModal) {
      unsavedCloseDialog.onOpen();
    } else {
      onClose();
    }
  }, [isDirtyModal, unsavedCloseDialog, onClose]);

  const handleConfirmClose = useCallback(async (saveFirst) => {
    setClosingWithSave(true);
    try {
      if (saveFirst) {
        await saveAllPagesEtiquetas();
      }
      unsavedCloseDialog.onClose();
      onClose();
    } finally {
      setClosingWithSave(false);
    }
  }, [saveAllPagesEtiquetas, onClose, unsavedCloseDialog]);

  const handleDeletePageRequest = useCallback((pageId) => {
    setPageIdToDelete(pageId);
    deleteConfirmDialog.onOpen();
  }, [deleteConfirmDialog]);

  const handleConfirmDelete = useCallback(async (saveFirst) => {
    if (!pageIdToDelete) return;
    setDeletingWithSave(true);
    try {
      if (saveFirst) {
        await saveAllPagesEtiquetas();
      }
      await deletePage(pageIdToDelete);
    } finally {
      setDeletingWithSave(false);
      setPageIdToDelete(null);
      deleteConfirmDialog.onClose();
    }
  }, [pageIdToDelete, saveAllPagesEtiquetas, deletePage, deleteConfirmDialog]);

  // ===================== AUTO-ETIQUETADO ASÍNCRONO =====================
  const useJobs = (import.meta.env.VITE_AUTO_ETIQUETAR_USE_JOBS ?? 'true') !== 'false';
  const [autoFase, setAutoFase] = useState('intro'); // 'intro' | 'progreso' | 'revision'
  const [autoJobId, setAutoJobId] = useState(null);
  const [autoJobEstado, setAutoJobEstado] = useState(null);
  const [autoSugerencias, setAutoSugerencias] = useState([]);
  const [autoSeleccion, setAutoSeleccion] = useState(() => new Set());
  const [autoStartedAt, setAutoStartedAt] = useState(null);
  const pollingRef = useRef(null);

  const stopPolling = useCallback(() => {
    if (pollingRef.current) {
      clearInterval(pollingRef.current);
      pollingRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const fetchEstado = useCallback(
    async (jobId) => {
      if (!detail?.id || !jobId) return;
      try {
        const r = await bandasAPI.getAutoEtiquetadoEstado(detail.id, jobId);
        const data = r.data || {};
        setAutoJobEstado(data.job || null);
        setAutoSugerencias(Array.isArray(data.paginas) ? data.paginas : []);
        const estado = data?.job?.estado;
        if (estado === 'COMPLETADO' || estado === 'FALLIDO' || estado === 'CANCELADO') {
          stopPolling();
          if (estado === 'COMPLETADO') setAutoFase('revision');
        }
      } catch (e) {
        // Silencioso: el siguiente tick reintentará
        // eslint-disable-next-line no-console
        console.warn('estado auto-etiquetado:', e?.message);
      }
    },
    [detail?.id, stopPolling],
  );

  const startPolling = useCallback(
    (jobId) => {
      stopPolling();
      pollingRef.current = setInterval(() => fetchEstado(jobId), 2000);
      // primer fetch inmediato
      fetchEstado(jobId);
    },
    [fetchEstado, stopPolling],
  );

  const handleConfirmAutoEtiquetadoLegacy = useCallback(async () => {
    if (!detail?.id) return;
    setAutoEtiquetando(true);
    try {
      const res = await bandasAPI.autoEtiquetarPaginasSugerencias(detail.id);
      const items = res.data?.paginas || [];
      const ok = items.filter((x) => !x.error);
      const fail = items.filter((x) => x.error);
      const orientacionRevisar = items
        .map((x) =>
          motivoRevisionOrientacion({
            orden: x.orden,
            errorMensaje: x.error,
            confianzaOrientacion: x.confianzaOrientacion,
          }),
        )
        .filter(Boolean);
      const sugMap = new Map(ok.map((x) => [x.paginaId, x]));
      setDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          paginas: prev.paginas.map((p) => {
            const s = sugMap.get(p.id);
            if (!s) return p;
            const rot = rotacionAplicableDesdeSugerencia({
              rotacionGrados: s.rotacionGrados,
              confianzaOrientacion: s.confianzaOrientacion ?? (s.error ? 0 : 0.92),
            });
            return {
              ...p,
              ...(rot !== null ? { rotacion: rot } : {}),
              instrumentosVoces:
                Array.isArray(s.instrumentosVoces) && s.instrumentosVoces.length
                  ? s.instrumentosVoces
                  : p.instrumentosVoces ?? [],
              tituloObra:
                s.tituloObra != null && String(s.tituloObra).trim() !== '' ? s.tituloObra.trim() : p.tituloObra,
              autor: s.autor != null && String(s.autor).trim() !== '' ? s.autor.trim() : p.autor,
              notasDirector:
                s.notasDirector != null && String(s.notasDirector).trim() !== ''
                  ? s.notasDirector.trim()
                  : p.notasDirector,
              ocrTexto: s.ocrTexto != null && String(s.ocrTexto).trim() !== '' ? s.ocrTexto.trim() : p.ocrTexto,
            };
          }),
        };
      });
      toaster.create({
        title: 'Sugerencias aplicadas en el editor',
        description: `${ok.length} página(s) con datos sugeridos.${
          fail.length ? ` ${fail.length} página(s) no se pudieron analizar.` : ''
        }${
          orientacionRevisar.length
            ? ` ${orientacionRevisar.length} página(s) requieren revisar la orientación manualmente.`
            : ''
        } Revisa cada etiqueta y guarda la obra cuando esté listo.`,
        type: fail.length || orientacionRevisar.length ? 'warning' : 'success',
        duration: 9000,
        closable: true,
      });
      autoInfoModal.onClose();
    } catch (e) {
      toaster.create({
        title: 'No se pudo analizar',
        description: e.response?.data?.message || e.message || 'Error',
        type: 'error',
        duration: 6000,
        closable: true,
      });
    } finally {
      setAutoEtiquetando(false);
    }
  }, [detail?.id, setDetail, autoInfoModal]);

  const handleConfirmAutoEtiquetadoAsync = useCallback(async () => {
    if (!detail?.id) return;
    setAutoEtiquetando(true);
    try {
      const r = await bandasAPI.iniciarAutoEtiquetado(detail.id, { reusarSiActivo: true });
      const jobId = r.data?.jobId;
      if (!jobId) throw new Error('No se obtuvo jobId');
      setAutoJobId(jobId);
      setAutoFase('progreso');
      setAutoStartedAt(Date.now());
      setAutoSugerencias([]);
      setAutoSeleccion(new Set());
      startPolling(jobId);
    } catch (e) {
      toaster.create({
        title: 'No se pudo iniciar',
        description: e.response?.data?.message || e.message || 'Error',
        type: 'error',
        duration: 6000,
        closable: true,
      });
    } finally {
      setAutoEtiquetando(false);
    }
  }, [detail?.id, startPolling]);

  const handleConfirmAutoEtiquetado = useCallback(() => {
    if (useJobs) return handleConfirmAutoEtiquetadoAsync();
    return handleConfirmAutoEtiquetadoLegacy();
  }, [useJobs, handleConfirmAutoEtiquetadoAsync, handleConfirmAutoEtiquetadoLegacy]);

  const handleCancelarAutoEtiquetado = useCallback(async () => {
    if (!detail?.id || !autoJobId) return;
    try {
      await bandasAPI.cancelarAutoEtiquetado(detail.id, autoJobId);
      stopPolling();
      toaster.create({ title: 'Auto-etiquetado cancelado', type: 'info', duration: 3000 });
      autoInfoModal.onClose();
      setAutoFase('intro');
    } catch (e) {
      toaster.create({
        title: 'No se pudo cancelar',
        description: e.response?.data?.message || e.message || 'Error',
        type: 'error',
        duration: 4000,
      });
    }
  }, [detail?.id, autoJobId, stopPolling, autoInfoModal]);

  const aplicarUnaSugerenciaLocal = useCallback(
    (paginaId, sug) => {
      const rot = rotacionAplicableDesdeSugerencia(sug);
      setDetail((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          paginas: prev.paginas.map((p) => {
            if (p.id !== paginaId) return p;
            return {
              ...p,
              ...(rot !== null ? { rotacion: rot } : {}),
              instrumentosVoces:
                Array.isArray(sug.instrumentosVoces) && sug.instrumentosVoces.length
                  ? sug.instrumentosVoces
                  : p.instrumentosVoces ?? [],
              tituloObra:
                sug.tituloObra != null && String(sug.tituloObra).trim() !== ''
                  ? sug.tituloObra.trim()
                  : p.tituloObra,
              autor: sug.autor != null && String(sug.autor).trim() !== '' ? sug.autor.trim() : p.autor,
              notasDirector:
                sug.notasDirector != null && String(sug.notasDirector).trim() !== ''
                  ? sug.notasDirector.trim()
                  : p.notasDirector,
              ocrTexto:
                sug.ocrTexto != null && String(sug.ocrTexto).trim() !== '' ? sug.ocrTexto.trim() : p.ocrTexto,
            };
          }),
        };
      });
    },
    [setDetail],
  );

  const handleAplicarUna = useCallback(
    async (sug) => {
      if (!detail?.id) return;
      aplicarUnaSugerenciaLocal(sug.paginaId, sug);
      try {
        const rot = rotacionAplicableDesdeSugerencia(sug);
        await bandasAPI.aplicarSugerenciaPagina(detail.id, sug.paginaId, {
          estado: 'APLICADO',
          decision: buildDecisionAplicarSugerencia(sug, rot),
        });
        setAutoSugerencias((prev) =>
          prev.map((x) => {
            const match =
              (sug.sugerenciaId && x.sugerenciaId && x.sugerenciaId === sug.sugerenciaId) ||
              (!sug.sugerenciaId && x.paginaId === sug.paginaId);
            return match ? { ...x, aplicadoEstado: 'APLICADO' } : x;
          }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('aplicar sugerencia:', e?.message);
      }
      setAutoSeleccion((prev) => {
        const n = new Set(prev);
        n.delete(sug.paginaId);
        return n;
      });
    },
    [detail?.id, aplicarUnaSugerenciaLocal],
  );

  const handleRechazarUna = useCallback(
    async (sug) => {
      if (!detail?.id) return;
      try {
        await bandasAPI.aplicarSugerenciaPagina(detail.id, sug.paginaId, { estado: 'RECHAZADO' });
        setAutoSugerencias((prev) =>
          prev.map((x) => {
            const match =
              (sug.sugerenciaId && x.sugerenciaId && x.sugerenciaId === sug.sugerenciaId) ||
              (!sug.sugerenciaId && x.paginaId === sug.paginaId);
            return match ? { ...x, aplicadoEstado: 'RECHAZADO' } : x;
          }),
        );
      } catch (e) {
        // eslint-disable-next-line no-console
        console.warn('rechazar sugerencia:', e?.message);
      }
      setAutoSeleccion((prev) => {
        const n = new Set(prev);
        n.delete(sug.paginaId);
        return n;
      });
    },
    [detail?.id],
  );

  const handleAplicarTodasVerdes = useCallback(async () => {
    const verdes = autoSugerencias.filter((s) => (s.confianza ?? 0) >= 0.85 && s.aplicadoEstado === 'PENDIENTE');
    if (!verdes.length) return;
    setAplicandoTodasVerdes(true);
    try {
      for (const s of verdes) {
        // eslint-disable-next-line no-await-in-loop
        await handleAplicarUna(s);
      }
      toaster.create({
        title: `${verdes.length} sugerencia(s) aplicadas`,
        description: 'Recuerda guardar la obra cuando termines.',
        type: 'success',
        duration: 5000,
      });
    } finally {
      setAplicandoTodasVerdes(false);
    }
  }, [autoSugerencias, handleAplicarUna]);

  const autoMetricas = useMemo(() => {
    const total = autoSugerencias.length;
    const altas = autoSugerencias.filter((s) => (s.confianza ?? 0) >= 0.85).length;
    const medias = autoSugerencias.filter((s) => (s.confianza ?? 0) >= 0.5 && (s.confianza ?? 0) < 0.85).length;
    const bajas = autoSugerencias.filter((s) => (s.confianza ?? 0) < 0.5).length;
    const aplicadas = autoSugerencias.filter((s) => s.aplicadoEstado === 'APLICADO' || s.aplicadoEstado === 'EDITADO').length;
    const pendientesVerdes = autoSugerencias.filter(
      (s) => (s.confianza ?? 0) >= 0.85 && s.aplicadoEstado === 'PENDIENTE',
    ).length;
    const orientacionOk = autoSugerencias.filter(
      (s) => (s.confianzaOrientacion ?? 0) >= UMBRAL_CONF_ORIENTACION,
    ).length;
    const orientacionRevisar = autoSugerencias
      .map((s) => motivoRevisionOrientacion(s))
      .filter(Boolean);
    return { total, altas, medias, bajas, aplicadas, pendientesVerdes, orientacionOk, orientacionRevisar };
  }, [autoSugerencias]);

  const idxPaginaSeleccionada = paginasOrdenadasEditor.findIndex((p) => p.id === selectedPage?.id);
  const totalPaginas = paginasOrdenadasEditor.length;
  const numPaginaUi = idxPaginaSeleccionada >= 0 ? idxPaginaSeleccionada + 1 : 1;

  return (
    <>
      <Dialog.Root open={isOpen} size='full' closeOnInteractOutside={false} onOpenChange={e => {
        if (!e.open) {
          handleCloseRequest();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content
              m={0}
              maxW="100vw"
              h="100vh"
              maxH="100vh"
              rounded="none"
              display="flex"
              flexDirection="column"
              overflow="hidden"
              bg={panelBg}>
              <Dialog.Header flexShrink={0} borderBottomWidth="1px" borderColor={railBorder} py={3} pe={onAnadirPaginas ? { base: '10.5rem', md: '11.5rem' } : { base: 14, md: 4 }}>
                <Flex align="flex-start" justify="space-between" gap={3} flexWrap="wrap">
                  <Box flex="1" minW={0} pe={2}>
                    <Heading size="md">Etiquetado de páginas</Heading>
                    {obraTitulo ? (
                      <Text fontSize="sm" color="gray.600" fontWeight="normal" lineClamp={2} mt={0.5}>
                        {obraTitulo}
                      </Text>
                    ) : null}
                    {isDirtyModal && (
                      <Text fontSize="xs" color="orange.500" fontWeight="semibold" mt={0.5}>
                        Cambios sin guardar
                      </Text>
                    )}
                  </Box>
                  <Button
                    size="sm"
                    variant="outline"
                    flexShrink={0}
                    onClick={handleCloseRequest}
                    display={{ base: 'none', lg: 'inline-flex' }}
                  >
                    <Icon as={FaArrowLeft} />
                    Cerrar
                  </Button>
                </Flex>
              </Dialog.Header>
              {typeof onAnadirPaginas === 'function' && (
                <Button
                  size="sm"
                  colorPalette="purple"
                  position="absolute"
                  top={3}
                  insetEnd={12}
                  zIndex={20}
                  onClick={onAnadirPaginas}
                >
                  <Icon as={FaPlus} />
                  Añadir páginas
                </Button>
              )}
              <Dialog.CloseTrigger asChild>
                <CloseButton
                  size="md"
                  position="absolute"
                  top={3}
                  insetEnd={3}
                  zIndex={20}
                  aria-label="Cerrar etiquetado"
                  onClick={handleCloseRequest}
                />
              </Dialog.CloseTrigger>
              <Dialog.Body flex="1" display="flex" flexDirection="column" p={0} overflow="hidden" minH={0} position="relative">
                {bulkActionStatus?.active && (
                  <Flex
                    position="absolute"
                    inset={0}
                    zIndex={15}
                    bg="blackAlpha.500"
                    align="center"
                    justify="center"
                    direction="column"
                    gap={3}
                  >
                    <Spinner size="xl" borderWidth="4px" color="white" />
                    <Text color="white" fontSize="sm" fontWeight="600">
                      {bulkActionStatus.label || 'Procesando...'}
                    </Text>
                  </Flex>
                )}
                {selectedPage ? (
                  <Flex flex="1" minH={0} direction={{ base: 'column', lg: 'row' }} overflow="hidden" align="stretch">
                    {/* Panel etiquetado (izquierda) */}
                    <Box
                      w={{ base: '100%', lg: 'min(340px, 34vw)' }}
                      minW={{ lg: '260px' }}
                      minH={0}
                      maxH={{ base: '42vh', lg: '100%' }}
                      h={{ base: 'auto', lg: '100%' }}
                      borderRightWidth={{ lg: '1px' }}
                      borderBottomWidth={{ base: '1px', lg: '0' }}
                      borderColor={railBorder}
                      overflowY="auto"
                      p={4}
                      flexShrink={0}
                      bg={railBg}
                    >
                      <VStack align="stretch" gap={3}>
                        <Fieldset.Root>
                          <Fieldset.Legend>Ámbito</Fieldset.Legend>
                          <RadioGroup.Root
                            value={ambitoPagina}
                            onValueChange={(v) => {
                              if (v === 'rango') initRangoDesdePagina(selectedPage);
                              setAmbitoPagina(v);
                            }}>
                            <Stack direction={{ base: 'column', sm: 'row' }} gap={4}>
                              <RadioGroup.Item value="individual"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Página individual</RadioGroup.ItemText></RadioGroup.Item>
                              <RadioGroup.Item value="rango"><RadioGroup.ItemHiddenInput /><RadioGroup.ItemIndicator /><RadioGroup.ItemText>Rango de páginas</RadioGroup.ItemText></RadioGroup.Item>
                            </Stack>
                          </RadioGroup.Root>
                        </Fieldset.Root>
                        {ambitoPagina === 'rango' && (
                          <Field.Root>
                            <Field.Label>Órdenes de página (rango)</Field.Label>
                            <Input
                              size="sm"
                              placeholder="Ej. 2-26 o 2-5, 7-8"
                              value={rangoOrdenesTexto}
                              onChange={(e) => setRangoOrdenesTexto(e.target.value)}
                            />
                            <Text fontSize="xs" color="gray.500" mt={1}>
                              Usa el número de orden de cada página. Un tramo «inicio-fin»; varios tramos separados por
                              comas (p. ej. si el PDF salta de página).
                            </Text>
                          </Field.Root>
                        )}
                        {/* Instrumentos con sus voces propias */}
                        {(() => {
                          const currentEntries = Array.isArray(
                            ambitoPagina === 'individual'
                              ? selectedPage.instrumentosVoces
                              : rangoPatch.instrumentosVoces,
                          )
                            ? (ambitoPagina === 'individual' ? selectedPage.instrumentosVoces : rangoPatch.instrumentosVoces)
                            : [];

                          const updateEntries = (entries) => {
                            if (ambitoPagina === 'individual') {
                              const pid = selectedPage?.id;
                              if (!pid) return;
                              setDetail((prev) => {
                                if (!prev?.paginas) return prev;
                                return {
                                  ...prev,
                                  paginas: prev.paginas.map((x) =>
                                    x.id === pid ? { ...x, instrumentosVoces: entries } : x,
                                  ),
                                };
                              });
                            } else {
                              setRangoPatch((rp) => ({ ...rp, instrumentosVoces: entries }));
                            }
                          };

                          const addInstrumento = (slug, nombre) => {
                            if (currentEntries.some((e) => e.slug === slug)) return;
                            const isDirector = slug === 'director' || etiquetaSlugDesdeNombre(nombre) === 'director';
                            const voces =
                              isDirector && vocesCatalog.some((v) => slugVozFila(v) === 'guion')
                                ? ['guion']
                                : [];
                            updateEntries([...currentEntries, { slug, nombre, voces }]);
                          };

                          const removeInstrumento = (slug) => {
                            updateEntries(currentEntries.filter((e) => e.slug !== slug));
                          };

                          const toggleVoz = (instSlug, vozSlug) => {
                            updateEntries(
                              currentEntries.map((e) =>
                                e.slug !== instSlug
                                  ? e
                                  : {
                                      ...e,
                                      voces: e.voces.includes(vozSlug)
                                        ? e.voces.filter((v) => v !== vozSlug)
                                        : [...e.voces, vozSlug],
                                    },
                              ),
                            );
                          };

                          const usedSlugs = new Set(currentEntries.map((e) => e.slug));
                          const available = instrumentCatalog.filter(
                            (i) => !usedSlugs.has(etiquetaSlugDesdeNombre(i.nombre)),
                          );

                          return (
                            <MultiCheckboxField
                              label="Instrumentos y voces"
                              labelProps={{ fontWeight: 'medium' }}
                            >
                              <Text fontSize="xs" color="gray.500" mb={2}>
                                Añade cada instrumento de esta página y sus voces (si aplica). Así "Flauta 1ª" y "Oboe 2ª"
                                quedan perfectamente diferenciados.
                              </Text>

                              {/* Entradas existentes */}
                              <VStack align="stretch" gap={2} mb={2}>
                                {currentEntries.map((entry, entryIdx) => (
                                  <Box
                                    key={`${selectedPage?.id || 'rango'}-iv-${entryIdx}-${entry.slug}`}
                                    borderWidth="1px"
                                    rounded="md"
                                    p={2}
                                    bg="purple.50"
                                    _dark={{ bg: 'purple.900' }}
                                  >
                                    <HStack justify="space-between" mb={vocesCatalog.length ? 1 : 0}>
                                      <Text fontSize="sm" fontWeight="600">{entry.nombre}</Text>
                                      <Button size="xs" variant="ghost" colorPalette="red" onClick={() => removeInstrumento(entry.slug)}>
                                        ✕
                                      </Button>
                                    </HStack>
                                    {vocesCatalog.length > 0 && (
                                      <CheckboxGroup value={entry.voces} onValueChange={(vals) => updateEntries(
                                        currentEntries.map((e) => e.slug === entry.slug ? { ...e, voces: vals } : e)
                                      )}>
                                        <Stack direction="row" flexWrap="wrap" gap={2}>
                                          {vocesCatalog.map((v) => {
                                            const slug = slugVozFila(v);
                                            return (
                                              <Checkbox.Root key={v.id} value={slug} size="sm"><Checkbox.HiddenInput /><Checkbox.Control><Checkbox.Indicator /></Checkbox.Control><Checkbox.Label>
                                                  {v.nombre}
                                                </Checkbox.Label></Checkbox.Root>
                                            );
                                          })}
                                        </Stack>
                                      </CheckboxGroup>
                                    )}
                                  </Box>
                                ))}
                              </VStack>

                              {/* Selector para añadir instrumento */}
                              {available.length > 0 ? (
                                <NativeSelect.Root>
                                  <NativeSelect.Field
                                    size="sm"
                                    placeholder="+ Añadir instrumento…"
                                    value=""
                                    onChange={(e) => {
                                      const slug = e.target.value;
                                      if (!slug) return;
                                      const row = instrumentCatalog.find((i) => etiquetaSlugDesdeNombre(i.nombre) === slug);
                                      addInstrumento(slug, row?.nombre ?? slug);
                                      e.target.value = '';
                                    }}>
                                    {available.map((i) => {
                                      const s = etiquetaSlugDesdeNombre(i.nombre);
                                      return (
                                        <option key={s} value={s}>
                                          {i.nombre}{i.esPersonalizado ? ' (pers.)' : ''}
                                        </option>
                                      );
                                    })}
                                  </NativeSelect.Field>
                                  <NativeSelect.Indicator />
                                </NativeSelect.Root>
                              ) : (
                                <Text fontSize="xs" color="gray.500" mb={1}>
                                  No quedan instrumentos del catálogo por añadir en esta página. Crea uno nuevo abajo o
                                  retira alguno de la lista.
                                </Text>
                              )}

                              <Alert.Root
                                status="info"
                                variant='subtle'
                                mt={3}
                                borderRadius="lg"
                                borderStartWidth='3px'
                                borderStartColor='colorPalette.solid'>
                                <Alert.Indicator />
                                <Box>
                                  <Text fontSize="sm" fontWeight="800">
                                    ¿No encuentras tu instrumento o voz?
                                  </Text>
                                  <Text fontSize="xs" color="gray.600">
                                    Puedes{' '}
                                    <Button
                                      size="xs"
                                      variant='plain'
                                      colorPalette="purple"
                                      fontWeight="800"
                                      onClick={() => addMaestroModal.onOpen()}
                                      disabled={!crearVozMaestro && !crearInstrumentoMaestro}
                                    >
                                      añádelas al listado
                                    </Button>
                                    .
                                  </Text>
                                </Box>
                              </Alert.Root>
                            </MultiCheckboxField>
                          );
                        })()}
                        <Field.Root>
                          <Field.Label>Notas director (página)</Field.Label>
                          <Textarea
                            size="sm"
                            rows={2}
                            value={
                              ambitoPagina === 'individual' ? selectedPage.notasDirector || '' : rangoPatch.notasDirector
                            }
                            onChange={(e) => {
                              const val = e.target.value;
                              if (ambitoPagina === 'individual') {
                                const pid = selectedPage?.id;
                                if (!pid) return;
                                setDetail((prev) => {
                                  if (!prev?.paginas) return prev;
                                  return {
                                    ...prev,
                                    paginas: prev.paginas.map((x) =>
                                      x.id === pid ? { ...x, notasDirector: val } : x,
                                    ),
                                  };
                                });
                              } else {
                                setRangoPatch((rp) => ({ ...rp, notasDirector: val }));
                              }
                            }}
                          />
                        </Field.Root>
                        {ambitoPagina === 'individual' ? (
                          <HStack gap={2} flexWrap="wrap" align="stretch">
                            <Button
                              size="sm"
                              colorPalette="purple"
                              onClick={() =>
                                savePage({
                                  instrumentosVoces: Array.isArray(selectedPage.instrumentosVoces) ? selectedPage.instrumentosVoces : [],
                                  notasDirector: selectedPage.notasDirector || null,
                                })
                              }
                              loading={saving}
                            >
                              Guardar página
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              colorPalette="purple"
                              onClick={() => void saveAllPagesEtiquetas()}
                              loading={saving}
                            >
                              Guardar todas las páginas
                            </Button>
                          </HStack>
                        ) : (
                          <VStack align="stretch" gap={2}>
                            <Button
                              size="sm"
                              colorPalette="purple"
                              variant="solid"
                              onClick={aplicarEtiquetasVariosTramos}
                              loading={saving}><FaLayerGroup />Aplicar al rango
                                                      </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              colorPalette="purple"
                              onClick={() => void saveAllPagesEtiquetas()}
                              loading={saving}
                            >
                              Guardar todas las páginas
                            </Button>
                          </VStack>
                        )}
                      </VStack>
                    </Box>

                    {/* Miniaturas: minH={0} para que el flex permita scroll; listado vertical en escritorio */}
                    <Box
                      w={{ base: '100%', lg: '168px' }}
                      minW={{ lg: '148px' }}
                      minH={0}
                      h={{ base: 'auto', lg: '100%' }}
                      maxH={{ base: '132px', lg: '100%' }}
                      borderRightWidth={{ lg: '1px' }}
                      borderBottomWidth={{ base: '1px', lg: '0' }}
                      borderColor={railBorder}
                      overflowX={{ base: 'auto', lg: 'hidden' }}
                      overflowY={{ base: 'hidden', lg: 'auto' }}
                      flexShrink={0}
                      bg={railBg}
                      p={2}
                      alignSelf="stretch"
                    >
                      <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                        <SortableContext items={pageIds} strategy={verticalListSortingStrategy}>
                          <Flex
                            direction={{ base: 'row', lg: 'column' }}
                            align="stretch"
                            gap={2}
                            minW={{ base: 'max-content', lg: 'auto' }}
                            w={{ lg: 'full' }}
                          >
                            {paginasOrdenadasEditor.map((p) => {
                              const ivEntries = Array.isArray(p.instrumentosVoces) ? p.instrumentosVoces : [];
                              const instLabel = ivEntries
                                .map((e) => {
                                  const vocesPart = e.voces?.length ? ` (${e.voces.map((v) => nombreVozPorEtiqueta(v) || v).join(', ')})` : '';
                                  return `${e.nombre}${vocesPart}`;
                                })
                                .join(' / ');
                              return (
                                <SortableThumbnail
                                  key={p.id}
                                  page={p}
                                  isSelected={selectedPageId === p.id}
                                  etiquetaResumen={instLabel}
                                  onClick={() => setSelectedPageId(p.id)}
                                />
                              );
                            })}
                          </Flex>
                        </SortableContext>
                      </DndContext>
                    </Box>

                    {/* Vista grande + barra inferior estilo lector PDF */}
                    <Flex direction="column" flex="1" minW={0} minH={0} bg="gray.100" _dark={{ bg: 'gray.950' }}>
                      <Box
                        flex="1"
                        minH={0}
                        overflow="auto"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        p={{ base: 2, md: 4 }}
                        cursor="zoom-in"
                        role="button"
                        tabIndex={0}
                        title="Pulsa para ver en grande"
                        onClick={() => setVisorPartituraAbierto(true)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            setVisorPartituraAbierto(true);
                          }
                        }}
                      >
                        <ChakraImage
                          src={pageImageSrc(selectedPage)}
                          alt="Vista"
                          maxH="100%"
                          maxW="full"
                          w="auto"
                          h="auto"
                          objectFit="contain"
                          transform={`rotate(${selectedPage.rotacion || 0}deg)`}
                          pointerEvents="none"
                        />
                      </Box>
                      <Flex
                        as="footer"
                        role="contentinfo"
                        aria-label="Herramientas de página"
                        flexShrink={0}
                        align="center"
                        justify="space-between"
                        flexWrap="wrap"
                        gap={3}
                        px={{ base: 3, md: 5 }}
                        py={3}
                        minH="56px"
                        bg={viewerFooterBg}
                        borderTopWidth="1px"
                        borderTopColor={viewerFooterBorder}
                      >
                        <HStack gap={2} flexWrap="wrap">
                          <Button
                            size="sm"
                            colorPalette="purple"
                            onClick={(e) => {
                              e.stopPropagation();
                              rotatePage();
                            }}
                            loading={saving}><FaSync />Rotar 90°
                                                  </Button>
                          <Button
                            size="sm"
                            colorPalette="blue"
                            variant="outline"
                            display={{ base: 'none', lg: 'inline-flex' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              void ordenarPaginasPorInstrumento();
                            }}
                            loading={ordenandoPorInstrumento}
                            disabled={saving || ordenandoPorInstrumento}><FaSortAmountDownAlt />Ordenar por instrumento
                                                  </Button>
                          <Button
                            size="sm"
                            colorPalette="purple"
                            variant="outline"
                            onClick={(e) => {
                              e.stopPropagation();
                              rotateAllPages90();
                            }}
                            loading={saving}><FaLayerGroup />Rotar todas 90°
                                                  </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            color="red.100"
                            borderColor="red.400"
                            _hover={{ bg: 'whiteAlpha.200' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeletePageRequest(selectedPage.id);
                            }}><FaTrash />Eliminar página
                                                  </Button>
                        </HStack>
                        <HStack gap={4} align="baseline" flexWrap="wrap" justify="flex-end">
                          <Text
                            fontSize="md"
                            fontWeight="800"
                            color="white"
                            fontVariantNumeric="tabular-nums"
                            letterSpacing="0.02em"
                            userSelect="none"
                          >
                            {numPaginaUi} / {totalPaginas}
                          </Text>
                          <Text fontSize="sm" color="gray.400" userSelect="none">
                            Orden guardado: <Text as="span" color="gray.200" fontWeight="600">{selectedPage.orden}</Text>
                          </Text>
                        </HStack>
                      </Flex>
                    </Flex>
                  </Flex>
                ) : (
                  <Text p={6} fontSize="sm" color="gray.500">
                    No hay páginas en esta obra.
                  </Text>
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root
        open={autoInfoModal.open}
        placement="center"
        size={autoFase === 'intro' ? 'md' : autoFase === 'progreso' ? 'lg' : 'xl'}
        closeOnInteractOutside={!autoEtiquetando && autoFase !== 'progreso'}
        closeOnEscape={!autoEtiquetando && autoFase !== 'progreso'}
        onOpenChange={e => {
          if (!e.open) {
            (autoEtiquetando || autoFase === 'progreso' ? () => {} : autoInfoModal.onClose)();
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header>
                {autoFase === 'intro' && 'Etiquetado automático'}
                {autoFase === 'progreso' && 'Analizando partituras…'}
                {autoFase === 'revision' && 'Revisa las sugerencias'}
              </Dialog.Header>
              <Dialog.CloseTrigger asChild>
                <CloseButton
                  size="sm"
                  disabled={autoEtiquetando || autoFase === 'progreso'}
                  aria-label="Cerrar"
                />
              </Dialog.CloseTrigger>
              <Dialog.Body maxH="70vh" overflowY="auto">
                {autoFase === 'intro' && (
                  <>
                    <Alert.Root
                      status="info"
                      variant='subtle'
                      borderRadius="md"
                      mb={4}
                      borderStartWidth='3px'
                      borderStartColor='colorPalette.solid'>
                      <Alert.Indicator />
                      <Box>
                        <Text fontWeight="semibold" mb={1}>Cómo funciona</Text>
                        <Text fontSize="sm" color="gray.700">
                          Analizamos cada página con IA visual (GPT-4o) usando exclusivamente los slugs de tu catálogo de instrumentos y voces. Verás el progreso en tiempo real y luego podrás aceptar, editar o rechazar cada sugerencia.
                        </Text>
                        <Text fontSize="sm" color="gray.700" mt={2}>
                          El proceso es <strong>asíncrono y reanudable</strong>: si cierras esta ventana o cae la conexión, el trabajo sigue en el servidor y podrás recuperarlo.
                        </Text>
                      </Box>
                    </Alert.Root>
                    {autoEtiquetando && (
                      <Flex align="center" gap={3} py={2}>
                        <Spinner color="purple.500" />
                        <Text fontSize="sm" color="gray.600">Encolando trabajo…</Text>
                      </Flex>
                    )}
                  </>
                )}

                {autoFase === 'progreso' && (
                  <VStack align="stretch" gap={4}>
                    <Box>
                      <Flex justify="space-between" align="center" mb={1}>
                        <Text fontSize="sm" color="gray.600">
                          {autoJobEstado?.progresoPaginas ?? 0} / {autoJobEstado?.totalPaginas ?? 0} páginas
                        </Text>
                        <Badge colorPalette={
                          autoJobEstado?.estado === 'COMPLETADO' ? 'green' :
                          autoJobEstado?.estado === 'FALLIDO' ? 'red' :
                          autoJobEstado?.estado === 'CANCELADO' ? 'gray' : 'purple'
                        }>{autoJobEstado?.estado || 'PENDIENTE'}</Badge>
                      </Flex>
                      <Progress.Root
                        value={
                          autoJobEstado?.totalPaginas
                            ? Math.round(((autoJobEstado?.progresoPaginas ?? 0) / autoJobEstado.totalPaginas) * 100)
                            : 0
                        }
                        colorPalette="purple"
                        size="md"
                        striped
                        animated>
                        <Progress.Track>
                          <Progress.Range />
                        </Progress.Track>
                      </Progress.Root>
                      {autoStartedAt && (
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Iniciado hace {Math.max(1, Math.round((Date.now() - autoStartedAt) / 1000))}s
                        </Text>
                      )}
                    </Box>
                    <Box maxH="50vh" overflowY="auto" borderWidth="1px" borderRadius="md" p={2}>
                      <Text fontSize="xs" color="gray.500" mb={1}>Páginas detectadas:</Text>
                      {autoSugerencias.length === 0 ? (
                        <Flex align="center" gap={2} p={3}>
                          <Spinner size="sm" /> <Text fontSize="sm" color="gray.600">Esperando primera página…</Text>
                        </Flex>
                      ) : (
                        <VStack align="stretch" gap={1}>
                          {autoSugerencias.map((s) => (
                            <Flex key={s.sugerenciaId} align="center" gap={2} fontSize="sm">
                              <Tag.Root size="sm" colorPalette="gray">#{s.orden}</Tag.Root>
                              <Tag.Root size="sm" colorPalette="purple">{s.tipoPagina}</Tag.Root>
                              <Box flex="1" lineClamp={1}>
                                {(Array.isArray(s.instrumentosVoces) && s.instrumentosVoces.length)
                                  ? s.instrumentosVoces.map((iv) => `${iv.nombre || iv.slug}${iv.voces?.length ? ' ('+iv.voces.join(',')+')' : ''}`).join(' · ')
                                  : (s.errorMensaje ? <Text color="red.500">{s.errorMensaje}</Text> : (s.paginaContinuacion ? '(continuación)' : '—'))}
                              </Box>
                              {(s.confianzaOrientacion ?? 0) >= UMBRAL_CONF_ORIENTACION &&
                              s.rotacionGrados &&
                              s.rotacionGrados !== 0 ? (
                                <Badge colorPalette="blue" fontSize="0.65rem">
                                  ↻ {s.rotacionGrados}°
                                </Badge>
                              ) : null}
                              <Badge colorPalette={
                                (s.confianza ?? 0) >= 0.85 ? 'green' :
                                (s.confianza ?? 0) >= 0.5 ? 'yellow' : 'red'
                              }>
                                Etq. {Math.round((s.confianza ?? 0) * 100)}%
                              </Badge>
                              {(s.confianzaOrientacion ?? 0) > 0 ? (
                                <Badge
                                  colorPalette={
                                    s.confianzaOrientacion >= UMBRAL_CONF_ORIENTACION ? 'cyan' : 'orange'
                                  }
                                  fontSize="0.65rem"
                                >
                                  Orient. {Math.round(s.confianzaOrientacion * 100)}%
                                </Badge>
                              ) : null}
                            </Flex>
                          ))}
                        </VStack>
                      )}
                    </Box>
                  </VStack>
                )}

                {autoFase === 'revision' && (
                  <VStack align="stretch" gap={3}>
                    <Wrap gap={2}>
                      <WrapItem><Tag.Root colorPalette="green">Verde ≥85%: {autoMetricas.altas}</Tag.Root></WrapItem>
                      <WrapItem><Tag.Root colorPalette="yellow">Ámbar 50-84%: {autoMetricas.medias}</Tag.Root></WrapItem>
                      <WrapItem><Tag.Root colorPalette="red">Roja &lt;50%: {autoMetricas.bajas}</Tag.Root></WrapItem>
                      <WrapItem><Tag.Root colorPalette="purple">Aplicadas: {autoMetricas.aplicadas}/{autoMetricas.total}</Tag.Root></WrapItem>
                      <WrapItem>
                        <Tag.Root colorPalette="cyan">
                          Orientación OK: {autoMetricas.orientacionOk}/{autoMetricas.total}
                        </Tag.Root>
                      </WrapItem>
                      {autoMetricas.orientacionRevisar.length > 0 ? (
                        <WrapItem>
                          <Tag.Root colorPalette="orange">Revisar orientación: {autoMetricas.orientacionRevisar.length}</Tag.Root>
                        </WrapItem>
                      ) : null}
                    </Wrap>
                    {autoMetricas.orientacionRevisar.length > 0 ? (
                      <Alert.Root status="warning" borderRadius="md" fontSize="sm">
                        <Alert.Indicator />
                        <Box>
                          <Text fontWeight="600">Páginas con orientación dudosa</Text>
                          <Text mt={1}>
                            {autoMetricas.orientacionRevisar.map((p) => `#${p.orden}`).join(', ')} — comprueba la rotación
                            manualmente (botón girar en el visor).
                          </Text>
                        </Box>
                      </Alert.Root>
                    ) : null}
                    <HStack>
                      <Button
                        size="sm"
                        colorPalette="green"
                        onClick={() => void handleAplicarTodasVerdes()}
                        disabled={autoMetricas.pendientesVerdes === 0 || aplicandoTodasVerdes}
                        loading={aplicandoTodasVerdes}
                        loadingText="Aplicando…"
                      >
                        Aceptar todas las verdes ({autoMetricas.pendientesVerdes})
                      </Button>
                    </HStack>
                    <Box maxH="60vh" overflowY="auto" borderWidth="1px" borderRadius="md">
                      <VStack align="stretch" gap={0}>{autoSugerencias.map((s) => {
                          const conf = s.confianza ?? 0;
                          const color = conf >= 0.85 ? 'green' : conf >= 0.5 ? 'yellow' : 'red';
                          return (
                            <Flex key={s.sugerenciaId} p={3} gap={3} align="center">
                              <Tag.Root colorPalette="gray">#{s.orden}</Tag.Root>
                              <Tag.Root colorPalette="purple">{s.tipoPagina}</Tag.Root>
                              <Box flex="1">
                                <Text fontSize="sm">
                                  {(Array.isArray(s.instrumentosVoces) && s.instrumentosVoces.length)
                                    ? s.instrumentosVoces.map((iv) => `${iv.nombre || iv.slug}${iv.voces?.length ? ' ('+iv.voces.join(',')+')' : ''}`).join(' · ')
                                    : (s.paginaContinuacion ? '(continuación de la anterior)' : '—')}
                                </Text>
                                {(s.tituloObra || s.autor) && (
                                  <Text fontSize="xs" color="gray.600" mt={0.5}>
                                    {[s.tituloObra, s.autor].filter(Boolean).join(' — ')}
                                  </Text>
                                )}
                                {s.errorMensaje && <Text fontSize="xs" color="red.500" mt={0.5}>{s.errorMensaje}</Text>}
                              </Box>
                              <Badge colorPalette={color}>{Math.round(conf * 100)}%</Badge>
                              <Badge colorPalette={
                                s.aplicadoEstado === 'APLICADO' ? 'green' :
                                s.aplicadoEstado === 'EDITADO' ? 'blue' :
                                s.aplicadoEstado === 'RECHAZADO' ? 'gray' : 'purple'
                              }>{s.aplicadoEstado}</Badge>
                              <HStack gap={1}>
                                <Button
                                  size="xs"
                                  colorPalette="green"
                                  onClick={() => void handleAplicarUna(s)}
                                  disabled={s.aplicadoEstado === 'APLICADO' || aplicandoTodasVerdes}
                                >
                                  Aceptar
                                </Button>
                                <Button
                                  size="xs"
                                  colorPalette="red"
                                  variant="outline"
                                  onClick={() => void handleRechazarUna(s)}
                                  disabled={s.aplicadoEstado === 'RECHAZADO' || aplicandoTodasVerdes}
                                >
                                  Rechazar
                                </Button>
                              </HStack>
                            </Flex>
                          );
                        })}</VStack>
                    </Box>
                    <Alert.Root status="info" borderRadius="md">
                      <Alert.Indicator />
                      <Text fontSize="sm">Las decisiones se guardan en el servidor (para mejorar el sistema) y también en el editor. No olvides pulsar «Guardar cambios» al cerrar.</Text>
                    </Alert.Root>
                  </VStack>
                )}
              </Dialog.Body>
              <Dialog.Footer>
                {autoFase === 'intro' && (
                  <>
                    <Button variant="ghost" mr={3} onClick={autoInfoModal.onClose} disabled={autoEtiquetando}>
                      Cancelar
                    </Button>
                    <Button colorPalette="purple" onClick={handleConfirmAutoEtiquetado} loading={autoEtiquetando}>
                      {useJobs ? 'Iniciar análisis' : 'Entendido, analizar páginas'}
                    </Button>
                  </>
                )}
                {autoFase === 'progreso' && (
                  <Button colorPalette="red" variant="outline" onClick={handleCancelarAutoEtiquetado}>
                    Cancelar trabajo
                  </Button>
                )}
                {autoFase === 'revision' && (
                  <Button
                    colorPalette="purple"
                    onClick={() => {
                      if (autoMetricas.orientacionRevisar.length > 0) {
                        toaster.create({
                          title: 'Revisa la orientación de algunas páginas',
                          description: `${autoMetricas.orientacionRevisar.length} página(s) no se orientaron automáticamente (confianza < 85 %).`,
                          type: 'warning',
                          duration: 8000,
                          closable: true,
                        });
                      }
                      autoInfoModal.onClose();
                      setAutoFase('intro');
                    }}
                  >
                    Cerrar revisión
                  </Button>
                )}
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      <Dialog.Root open={addMaestroModal.open} placement='center' size='md' onOpenChange={e => {
        if (!e.open) {
          addMaestroModal.onClose();
        }
      }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content mx={4}>
              <Dialog.Header>Añadir al listado</Dialog.Header>
              <Dialog.CloseTrigger asChild>
                <CloseButton size="sm" aria-label="Cerrar" />
              </Dialog.CloseTrigger>
              <Dialog.Body>
                <VStack align="stretch" gap={4}>
                  <Box>
                    <Text fontSize="sm" fontWeight="700" mb={2}>
                      Nueva voz
                    </Text>
                    <HStack gap={2}>
                      <Input
                        size="sm"
                        flex={1}
                        placeholder="Ej. Soprano, Tenor…"
                        value={nuevaVozMaestroNombre}
                        onChange={(e) => setNuevaVozMaestroNombre(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void ejecutarCrearVozMaestro();
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        colorPalette="blue"
                        onClick={() => void ejecutarCrearVozMaestro()}
                        loading={creandoVozMaestro}
                        disabled={!crearVozMaestro}><FaPlus />Añadir
                                          </Button>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      La voz aparecerá al instante en el etiquetado para asignarla a un instrumento.
                    </Text>
                  </Box>

                  <Separator />

                  <Box>
                    <Text fontSize="sm" fontWeight="700" mb={2}>
                      Nuevo instrumento (personalizado)
                    </Text>
                    <HStack gap={2}>
                      <Input
                        size="sm"
                        flex={1}
                        placeholder="Ej. Caja, Bombardino…"
                        value={nuevoInstMaestroNombre}
                        onChange={(e) => setNuevoInstMaestroNombre(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            void ejecutarCrearInstrumentoMaestro();
                          }
                        }}
                      />
                      <Button
                        size="sm"
                        colorPalette="purple"
                        onClick={() => void ejecutarCrearInstrumentoMaestro()}
                        loading={creandoInstMaestro}
                        disabled={!crearInstrumentoMaestro}><FaPlus />Añadir
                                          </Button>
                    </HStack>
                    <Text fontSize="xs" color="gray.500" mt={2}>
                      Se añadirá como instrumento personalizado de la banda y aparecerá en el desplegable de instrumentos.
                    </Text>
                  </Box>
                </VStack>
              </Dialog.Body>
              <Dialog.Footer>
                <Button variant="ghost" onClick={addMaestroModal.onClose}>
                  Cerrar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Advertencia de cambios sin guardar al cerrar */}
      <Dialog.Root
        open={unsavedCloseDialog.open}
        initialFocusEl={() => cancelUnsavedRef.current}
        placement='center'
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            unsavedCloseDialog.onClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="lg" fontWeight="bold">
                Cambios sin guardar
              </Dialog.Header>
              <Dialog.Body>
                <Alert.Root status="warning" rounded="md" mb={3}>
                  <Alert.Indicator />
                  Tienes etiquetas que no has guardado. Si cierras ahora, perderás esos cambios.
                </Alert.Root>
                <Text fontSize="sm" color="gray.600">
                  ¿Quieres guardar todo antes de salir, o cerrar sin guardar?
                </Text>
              </Dialog.Body>
              <Dialog.Footer gap={2} flexWrap="wrap">
                <Button ref={cancelUnsavedRef} onClick={unsavedCloseDialog.onClose} disabled={closingWithSave}>
                  Volver al etiquetado
                </Button>
                <Button
                  colorPalette="red"
                  variant="outline"
                  onClick={() => handleConfirmClose(false)}
                  loading={closingWithSave}
                >
                  Cerrar sin guardar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={() => handleConfirmClose(true)}
                  loading={closingWithSave}
                >
                  Guardar y cerrar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>

      {/* Confirmación antes de eliminar página */}
      <Dialog.Root
        open={deleteConfirmDialog.open}
        initialFocusEl={() => cancelDeleteRef.current}
        placement='center'
        role='alertdialog'
        onOpenChange={e => {
          if (!e.open) {
            deleteConfirmDialog.onClose();
          }
        }}>
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <Dialog.Header fontSize="lg" fontWeight="bold">
                Eliminar página
              </Dialog.Header>
              <Dialog.Body>
                <Alert.Root status="warning" rounded="md" mb={3}>
                  <Alert.Indicator />
                  Tienes cambios sin guardar. Si eliminas ahora perderás el etiquetado que no hayas guardado.
                </Alert.Root>
                <Text fontSize="sm" color="gray.600">
                  ¿Quieres guardar el etiquetado actual y luego eliminar la página, o eliminarla directamente sin guardar?
                </Text>
              </Dialog.Body>
              <Dialog.Footer gap={2} flexWrap="wrap">
                <Button ref={cancelDeleteRef} onClick={deleteConfirmDialog.onClose} disabled={deletingWithSave}>
                  Cancelar
                </Button>
                <Button
                  colorPalette="red"
                  variant="outline"
                  onClick={() => handleConfirmDelete(false)}
                  loading={deletingWithSave}
                >
                  Eliminar sin guardar
                </Button>
                <Button
                  colorPalette="purple"
                  onClick={() => handleConfirmDelete(true)}
                  loading={deletingWithSave}
                >
                  Guardar y eliminar
                </Button>
              </Dialog.Footer>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </>
  );
}
